/**
 * SocialPulseJoin — /join
 *
 * The public "Join SocialPulse" landing. Split layout:
 *   Left  : Sign in / Sign up card powered by AuthContext (Google + Email link).
 *   Right : A mini live preview of a real SocialPulse member profile —
 *           so first-time visitors immediately see what they get after
 *           signing up (identity chip, Social Capital, network sites bar,
 *           marketplace + events sample tiles).
 *
 * When the user is already signed in we redirect them to /pulse/{handle}
 * (or /onboarding/profile if the handle isn't set yet).
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Activity, ArrowRight, BadgeCheck, MapPin, Sparkles, Users, Trees, Utensils,
  GraduationCap, Newspaper, ShoppingBag, HeartHandshake, Loader2,
  ShieldCheck, ExternalLink,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import SocialPulseHeader from "@/components/socialpulse/SocialPulseHeader";
import SocialPulseFooter from "@/components/socialpulse/SocialPulseFooter";

const SAMPLE = {
  handle: "keralakid",
  name: "Rahul Menon",
  role: "Photographer · Kadanad, Kerala",
  avatar_bg: "linear-gradient(135deg,#059669 0%,#7C3AED 100%)",
  initial: "R",
  verified: true,
  capital: 1287,
  badges: ["Photographer", "Community Leader", "Village Voice"],
  network_sites: [
    { name: "NaturePulse",  sub: "Observe Nature",    icon: Trees,       href: "https://naturepulse.pro" },
    { name: "FoodPulse",    sub: "Recipes & Markets", icon: Utensils,    href: "https://foodpulse.net" },
    { name: "AlumniPulse",  sub: "Stay Connected",    icon: GraduationCap,href: "https://alumnipulse.pro" },
    { name: "NewsPulse",    sub: "Local + World",     icon: Newspaper,   href: "https://news.socialpulse.com" },
  ],
  marketplace: [
    { title: "Kadanad Handloom Stole", price: "₹1,200" },
    { title: "Cardamom · 250g",        price: "₹380"   },
  ],
  events: [
    { title: "Onam Community Photo Walk", when: "Sat · 6:30 AM" },
    { title: "Kadanad River Cleanup",     when: "Sun · 5:30 AM" },
  ],
};

function GoogleGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden>
      <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.44c-.28 1.51-1.13 2.79-2.42 3.65v3.03h3.91c2.29-2.11 3.56-5.22 3.56-8.92z"/>
      <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.94-2.91l-3.91-3.03c-1.08.73-2.47 1.15-4.03 1.15-3.09 0-5.71-2.09-6.64-4.9H1.31v3.08C3.29 21.3 7.31 24 12 24z"/>
      <path fill="#FBBC05" d="M5.36 14.31c-.24-.73-.38-1.5-.38-2.31s.14-1.58.38-2.31V6.61H1.31A11.98 11.98 0 0 0 0 12c0 1.93.46 3.75 1.31 5.39l4.05-3.08z"/>
      <path fill="#EA4335" d="M12 4.79c1.77 0 3.35.61 4.6 1.8l3.45-3.45C17.95 1.18 15.24 0 12 0 7.31 0 3.29 2.7 1.31 6.61l4.05 3.08C6.29 6.88 8.91 4.79 12 4.79z"/>
    </svg>
  );
}

export default function SocialPulseJoin() {
  const { user, loading, signInWithGoogleProvider, sendEmailLink } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy]   = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    document.title = "Join SocialPulse — sign up, claim your handle";
    if (!loading && user) {
      const handle = user.handle || user.username || null;
      nav(handle ? `/pulse/${handle}` : "/onboarding/profile", { replace: true });
    }
  }, [user, loading, nav]);

  const handleGoogle = async () => {
    setBusy(true);
    try {
      await signInWithGoogleProvider(false);
      toast.success("Signed in — welcome!");
    } catch (e) {
      toast.error(e?.message || "Google sign-in failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleEmail = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) return toast.error("Enter a valid email.");
    setBusy(true);
    try {
      await sendEmailLink(email.trim(), false);
      setEmailSent(true);
      toast.success("Check your inbox — we sent a magic link.");
    } catch (err) {
      toast.error(err?.message || "Couldn't send the link. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="sp-join-page">
      <SocialPulseHeader />

      {/* Ambient background */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="absolute -top-24 -left-20 w-96 h-96 rounded-full blur-3xl opacity-40 pointer-events-none"
          style={{ background: "radial-gradient(circle,#A7F3D0 0%,transparent 70%)" }}
        />
        <div
          aria-hidden
          className="absolute top-10 right-0 w-[28rem] h-[28rem] rounded-full blur-3xl opacity-40 pointer-events-none"
          style={{ background: "radial-gradient(circle,#DDD6FE 0%,transparent 75%)" }}
        />

        <div className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 py-10 sm:py-14 relative grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] gap-10">
          {/* ─── Left: sign-in / sign-up card ─────────────────── */}
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[.22em] text-violet-700">
              <Sparkles className="w-3.5 h-3.5" /> Join the network
            </div>
            <h1
              className="mt-3 font-serif font-bold tracking-tight text-slate-900 leading-[1.05] text-3xl sm:text-4xl lg:text-5xl"
              data-testid="sp-join-headline"
            >
              One identity.<br/>
              <span className="text-emerald-700">Many pulses.</span>
            </h1>
            <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed max-w-md">
              Sign up once and use the same account across every SocialPulse
              site — WorldPulse, USAPulse, Indias, Kerala.com, FoodPulse and 30+ more.
            </p>

            <div className="mt-8 rounded-3xl bg-white ring-1 ring-slate-200 shadow-sm overflow-hidden">
              <div className="p-6 sm:p-7">
                <h2 className="font-serif font-bold text-slate-900 text-lg">Sign in or create your profile</h2>
                <p className="text-sm text-slate-500 mt-1">No password to remember — Google or a magic link.</p>

                <button
                  type="button"
                  onClick={handleGoogle}
                  disabled={busy || loading}
                  data-testid="sp-join-google-btn"
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-white ring-1 ring-slate-300 hover:ring-slate-400 hover:bg-slate-50 text-slate-800 font-semibold px-5 py-2.5 text-sm transition-colors disabled:opacity-60"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <GoogleGlyph />}
                  Continue with Google
                </button>

                <div className="my-5 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-widest text-slate-400">
                  <span className="h-px flex-1 bg-slate-200" /> or <span className="h-px flex-1 bg-slate-200" />
                </div>

                {emailSent ? (
                  <div
                    data-testid="sp-join-magic-link-sent"
                    className="rounded-2xl bg-emerald-50 ring-1 ring-emerald-200 p-4 text-sm text-emerald-800"
                  >
                    ✉ Magic link sent to <b>{email}</b>. Open it on this device to sign in.
                  </div>
                ) : (
                  <form onSubmit={handleEmail} className="space-y-3" data-testid="sp-join-email-form">
                    <label className="block">
                      <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Your email</span>
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        data-testid="sp-join-email-input"
                        className="mt-1.5 w-full rounded-xl bg-white ring-1 ring-slate-300 focus:ring-violet-500 focus:outline-none px-4 py-2.5 text-sm text-slate-800 placeholder:text-slate-400"
                      />
                    </label>
                    <button
                      type="submit"
                      disabled={busy}
                      data-testid="sp-join-email-btn"
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold px-5 py-2.5 text-sm transition-colors shadow-sm disabled:opacity-60"
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                      Send me a magic link
                    </button>
                  </form>
                )}

                <p className="mt-5 text-[11px] leading-relaxed text-slate-500">
                  By continuing you agree to the{" "}
                  <Link to="/terms" className="text-violet-700 hover:underline">Terms</Link> and{" "}
                  <Link to="/privacy" className="text-violet-700 hover:underline">Privacy Policy</Link>.
                  We never share your data with sister sites without your consent.
                </p>
              </div>

              <div className="border-t border-slate-100 bg-slate-50 px-6 sm:px-7 py-4 flex items-center gap-2 text-xs text-slate-600">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                One SSO for every SocialPulse site — no separate accounts.
              </div>
            </div>

            <p className="mt-4 text-sm text-slate-500">
              Already signed in on another SocialPulse site?{" "}
              <Link to="/login" className="text-violet-700 font-semibold hover:underline" data-testid="sp-join-existing-link">
                Take me straight in →
              </Link>
            </p>
          </div>

          {/* ─── Right: live sample member preview ────────────── */}
          <aside data-testid="sp-join-sample-profile" className="lg:pl-4">
            <div className="text-xs font-semibold uppercase tracking-[.22em] text-slate-500 mb-3 flex items-center gap-2">
              <Users className="w-3.5 h-3.5" /> Sample profile · what you'll get
            </div>
            <div className="rounded-3xl bg-white ring-1 ring-slate-200 shadow-sm overflow-hidden">
              {/* Cover */}
              <div className="h-24 sm:h-28 bg-gradient-to-r from-emerald-100 via-violet-100 to-amber-100" />
              {/* Identity */}
              <div className="px-5 sm:px-6 -mt-10 pb-5 border-b border-slate-100">
                <div className="flex items-end gap-4">
                  <div
                    className="w-20 h-20 rounded-2xl grid place-items-center text-white font-serif font-bold text-3xl ring-4 ring-white shadow-md"
                    style={{ background: SAMPLE.avatar_bg }}
                  >
                    {SAMPLE.initial}
                  </div>
                  <div className="pb-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <h3 className="font-serif font-bold text-slate-900 text-xl truncate">{SAMPLE.name}</h3>
                      {SAMPLE.verified && <BadgeCheck className="w-4 h-4 text-violet-600 shrink-0" />}
                    </div>
                    <p className="text-sm text-slate-500 truncate">@{SAMPLE.handle}</p>
                    <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {SAMPLE.role}
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {SAMPLE.badges.map((b) => (
                    <span key={b} className="rounded-full bg-violet-50 ring-1 ring-violet-200 text-violet-700 px-2.5 py-0.5 text-[11px] font-semibold">
                      {b}
                    </span>
                  ))}
                </div>
              </div>

              {/* Social Capital */}
              <div className="grid grid-cols-3 divide-x divide-slate-100 border-b border-slate-100">
                <div className="p-4 text-center">
                  <div className="text-lg font-serif font-bold text-slate-900">{SAMPLE.capital}</div>
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Social Capital</div>
                </div>
                <div className="p-4 text-center">
                  <div className="text-lg font-serif font-bold text-emerald-700">98</div>
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Followers</div>
                </div>
                <div className="p-4 text-center">
                  <div className="text-lg font-serif font-bold text-violet-700">42</div>
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Following</div>
                </div>
              </div>

              {/* Network sites */}
              <div className="p-5 sm:p-6 border-b border-slate-100">
                <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-3">
                  <Activity className="w-3 h-3" /> Cross-site presence
                </div>
                <ul className="grid grid-cols-2 gap-2">
                  {SAMPLE.network_sites.map((s) => {
                    const Icon = s.icon;
                    return (
                      <li key={s.name}>
                        <a href={s.href} target="_blank" rel="noopener" className="flex items-center gap-2 rounded-xl bg-slate-50 hover:bg-slate-100 ring-1 ring-slate-200 p-2.5 group">
                          <span className="w-8 h-8 grid place-items-center rounded-lg bg-white text-emerald-700 ring-1 ring-slate-200"><Icon className="w-4 h-4" /></span>
                          <div className="min-w-0">
                            <div className="text-xs font-semibold text-slate-800 truncate">{s.name}</div>
                            <div className="text-[10px] text-slate-500 truncate">{s.sub}</div>
                          </div>
                          <ExternalLink className="ml-auto w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Marketplace + events */}
              <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
                <div className="p-5">
                  <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-3">
                    <ShoppingBag className="w-3 h-3" /> Marketplace
                  </div>
                  <ul className="space-y-2">
                    {SAMPLE.marketplace.map((m) => (
                      <li key={m.title} className="flex items-center justify-between text-sm">
                        <span className="text-slate-700 truncate mr-2">{m.title}</span>
                        <span className="font-semibold text-emerald-700 shrink-0">{m.price}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-3">
                    <HeartHandshake className="w-3 h-3" /> Events near me
                  </div>
                  <ul className="space-y-2">
                    {SAMPLE.events.map((e) => (
                      <li key={e.title} className="text-sm">
                        <div className="text-slate-700 truncate">{e.title}</div>
                        <div className="text-[11px] text-slate-500">{e.when}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <p className="mt-3 text-[11px] text-slate-500 flex items-center gap-1.5">
              <Activity className="w-3 h-3 text-violet-500" />
              This is a sample. Your profile is minted the moment you sign up.
            </p>
          </aside>
        </div>
      </section>

      <SocialPulseFooter />
    </div>
  );
}
